import type { InputProps } from './types'

import { forwardRef, useCallback, useMemo } from 'react'
import {
  type NumberFormatValues,
  NumericFormat,
  PatternFormat,
  type SourceInfo,
} from 'react-number-format'

import { createLogger } from '@/lib/logger'

import { getFormattingProps } from './helpers/get-formatting-props'
import { getInputClassName } from './helpers/get-input-classname'
import { Slot } from './helpers/slot'
import { useSlotPadding } from './hooks/use-slot-padding'

const LOGGER = createLogger('components/atomic/input-v2')

/**
 * @deprecated Use `Input` from `@moment/design-system-ui/input` instead.
 */
const InputV2 = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    type = 'text',
    value,
    onChange,
    onValueChange,
    className,
    hasError = false,
    getInputRef,
    isAllowed,
    placeholder,
    disabled,
    name,
    id,
    autoComplete,
    required,
    size,
    percentageScale = 'percent', // Default to 0-100 format for backward compatibility
    leftSlot,
    rightSlot,
    defaultValue,
    ...rest
  } = props

  // Handle slot padding calculations for both slots
  const { leftSlotRef, rightSlotRef, leftPadding, rightPadding } =
    useSlotPadding(leftSlot, rightSlot)

  // Generate input styling classes based on error state
  const inputClassName = useMemo(
    () => getInputClassName(hasError, className, size),
    [hasError, className, size],
  )

  // Get formatting config based on input type (currency, number, text, custom)
  const formattingProps = useMemo(
    () => getFormattingProps(type, props),
    [type, props],
  )

  // Common props shared across all inputs
  const commonProps = useMemo(
    () => ({
      placeholder,
      disabled,
      name,
      id,
      autoComplete,
      required,
      'data-lpignore': 'true',
    }),
    [placeholder, disabled, name, id, autoComplete, required],
  )

  // Transform percentage values based on scale
  const getTransformedValue = useCallback(() => {
    if (type !== 'percentage' || value === undefined || value === '') {
      return value
    }

    const numValue = Number(value)
    if (!Number.isFinite(numValue)) return value

    if (percentageScale === 'decimal') {
      const displayValue = numValue * 100
      const { decimalScale } = formattingProps

      return typeof decimalScale === 'number'
        ? Number(displayValue.toFixed(decimalScale))
        : displayValue
    }

    return value
  }, [type, value, percentageScale, formattingProps])

  // Handle text input changes (for unformatted inputs)
  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value, name)
    },
    [onChange, name],
  )

  // Handle formatted input changes (for numeric/pattern formatted inputs)
  const handleFormattedChange = useCallback(
    (values: NumberFormatValues, sourceInfo: SourceInfo) => {
      const { formattedValue, floatValue } = values

      // For percentage inputs, transform the value based on scale
      if (type === 'percentage' && floatValue !== undefined) {
        const transformedValue =
          percentageScale === 'decimal'
            ? floatValue / 100 // Convert from 0-100 to 0-1
            : floatValue // Keep as 0-100

        const transformedValues = {
          ...values,
          floatValue: transformedValue,
          value: transformedValue.toString(),
        }

        onValueChange?.(transformedValues, sourceInfo)
        onChange?.(transformedValue.toString(), name, transformedValues)
      } else {
        // Call both callbacks - onValueChange gets raw values, onChange gets formatted value
        onValueChange?.(values, sourceInfo)
        onChange?.(formattedValue, name, values)
      }
    },
    [onValueChange, onChange, name, type, percentageScale],
  )

  // Get separator height based on size
  const separatorHeight = size === 'small' ? '26px' : '30px'

  // Render standard text input (default behavior)
  const renderTextInput = () => {
    const input = (
      <input
        ref={ref}
        type="text"
        value={value ?? ''}
        defaultValue={defaultValue ?? undefined}
        onChange={handleTextChange}
        className={inputClassName}
        style={{
          paddingLeft: leftPadding ? `${leftPadding}px` : undefined,
          paddingRight: rightPadding ? `${rightPadding}px` : undefined,
        }}
        {...commonProps}
        {...rest}
      />
    )

    // If no slots, return plain input
    if (!leftSlot && !rightSlot) return input

    return (
      <div className="relative flex items-center w-full">
        {leftSlot && (
          <Slot
            ref={leftSlotRef}
            slotProps={leftSlot}
            side="left"
            size={size}
            separatorHeight={separatorHeight}
          />
        )}

        {input}

        {rightSlot && (
          <Slot
            ref={rightSlotRef}
            slotProps={rightSlot}
            side="right"
            size={size}
            separatorHeight={separatorHeight}
          />
        )}
      </div>
    )
  }

  // Render custom pattern formatted input (phone numbers, credit cards, etc.)
  const renderCustomInput = () => {
    if (!props.format) {
      LOGGER.warn('InputV2: format prop is required for custom type')
      return renderTextInput()
    }

    const input = (
      <PatternFormat
        getInputRef={getInputRef || ref}
        value={value}
        onValueChange={handleFormattedChange}
        className={inputClassName}
        isAllowed={isAllowed}
        // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
        format={formattingProps.format!}
        mask={formattingProps.mask}
        allowEmptyFormatting={formattingProps.allowEmptyFormatting}
        style={{
          paddingLeft: leftPadding ? `${leftPadding}px` : undefined,
          paddingRight: rightPadding ? `${rightPadding}px` : undefined,
        }}
        {...commonProps}
      />
    )

    // If no slots, return plain input
    if (!leftSlot && !rightSlot) return input

    return (
      <div className="relative flex items-center w-full">
        {leftSlot && (
          <Slot
            ref={leftSlotRef}
            slotProps={leftSlot}
            side="left"
            size={size}
            separatorHeight={separatorHeight}
          />
        )}

        {input}

        {rightSlot && (
          <Slot
            ref={rightSlotRef}
            slotProps={rightSlot}
            side="right"
            size={size}
            separatorHeight={separatorHeight}
          />
        )}
      </div>
    )
  }

  // Render numeric input with formatting (numbers, currency, percentages)
  const renderNumericInput = () => {
    const input = (
      <NumericFormat
        getInputRef={getInputRef || ref}
        value={getTransformedValue()}
        onValueChange={handleFormattedChange}
        className={inputClassName}
        isAllowed={isAllowed}
        style={{
          paddingLeft: leftPadding ? `${leftPadding}px` : undefined,
          paddingRight: rightPadding ? `${rightPadding}px` : undefined,
        }}
        {...formattingProps}
        {...commonProps}
      />
    )

    // If no slots, return plain input
    if (!leftSlot && !rightSlot) return input

    return (
      <div className="relative flex items-center w-full">
        {leftSlot && (
          <Slot
            ref={leftSlotRef}
            slotProps={leftSlot}
            side="left"
            size={size}
            separatorHeight={separatorHeight}
          />
        )}

        {input}

        {rightSlot && (
          <Slot
            ref={rightSlotRef}
            slotProps={rightSlot}
            side="right"
            size={size}
            separatorHeight={separatorHeight}
          />
        )}
      </div>
    )
  }

  // Map input types to their respective renderer functions
  const inputRenderers = {
    text: renderTextInput,
    custom: renderCustomInput,
    number: renderNumericInput,
    currency: renderNumericInput,
    percentage: renderNumericInput,
  } as const

  // Select appropriate renderer based on type, fallback to text input
  const renderer = inputRenderers[type] || renderTextInput
  return renderer()
})

InputV2.displayName = 'InputV2'

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default InputV2
